<script>
  import Layout from './Layout.svelte';
  import SettingsSaveBar from './SettingsSaveBar.svelte';
  import SettingsAlerts from './SettingsAlerts.svelte';
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { settingsStore } from '../stores/settingsStore.js';
  import { SETTINGS_TABS } from '../lib/settings/constants.js';

  const {
    loading,
    formData,
    formValid,
    isQuickBooks,
    managedByOwner,
    initializeSettings,
    saveSettings
  } = settingsStore;

  export let activeTab = '';
  export let tabTitle = '';
  export let showSave = false;
  export let suppressManagedNotice = false;

  // The two accounting tabs are mutually exclusive: QuickBooks users run
  // JobTread-only so Xero is irrelevant to them, and vice versa.
  $: visibleTabs = SETTINGS_TABS.filter((tab) => {
    if (tab.id === 'xero') return !$isQuickBooks;
    if (tab.id === 'quickbooks') return $isQuickBooks;
    return true;
  });
  $: currentTab = SETTINGS_TABS.find((tab) => tab.id === activeTab);
  $: resolvedShowSave = showSave || (currentTab?.showSave ?? false);

  onMount(async () => {
    await initializeSettings();
  });

  function goToTab(path) {
    navigate(path);
  }
</script>

<Layout activeRoute="settings">
  <div class="container mx-auto px-4 py-8">
    {#if $loading && !$formData.jobtreadApiKey}
      <div class="flex justify-center items-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <span class="ml-3 text-gray-600">Loading settings...</span>
      </div>
    {:else}
      <div class="bg-white rounded-lg shadow overflow-hidden">
        <div class="p-6">
          <h2 class="text-2xl font-bold mb-2">Settings</h2>
          <p class="text-md text-gray-800 mb-4">
            {#if $isQuickBooks}
              Complete your JobTread Connection and account details below. Data is only synced with your confirmation.
            {:else}
              Complete both JobTread and Xero Connections and account details below. Data is only synced with your confirmation.
            {/if}
          </p>

          <div class="flex flex-col lg:flex-row gap-6">
            <!-- Secondary nav -->
            <nav class="lg:w-56 flex-shrink-0">
              <div class="lg:hidden mb-4">
                <label for="settings-tab-select" class="sr-only">Settings section</label>
                <select
                  id="settings-tab-select"
                  class="w-full rounded-md border-gray-300 text-sm py-2 px-3"
                  value={currentTab?.path || ''}
                  on:change={(e) => goToTab(e.target.value)}
                >
                  {#each visibleTabs as tab}
                    <option value={tab.path}>{tab.label}</option>
                  {/each}
                </select>
              </div>

              <div class="hidden lg:block space-y-1">
                {#each visibleTabs as tab}
                  <button
                    type="button"
                    on:click={() => goToTab(tab.path)}
                    class="w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors border-0
                      {activeTab === tab.id
                        ? 'bg-blue-50 text-blue-700 border-l-[3px] border-l-blue-600'
                        : 'text-gray-700 hover:bg-gray-50 border-l-[3px] border-l-transparent'}"
                  >
                    {tab.label}
                  </button>
                {/each}
              </div>
            </nav>

            <!-- Tab content -->
            <div class="flex-1 min-w-0">
              {#if tabTitle}
                <div class="flex items-center justify-between gap-4 mb-4">
                  <h3 class="text-xl font-semibold text-gray-900">{tabTitle}</h3>
                  {#if resolvedShowSave && !$managedByOwner}
                    <SettingsSaveBar
                      variant="compact"
                      disabled={!$formValid}
                      loading={$loading}
                      onSave={saveSettings}
                    />
                  {/if}
                </div>
              {/if}

              {#if $managedByOwner && !suppressManagedNotice}
                <div class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p class="text-sm text-blue-800">
                    These settings are managed by your organisation owner and are shown read-only.
                    Contact your organisation owner to change them. You still connect Xero with
                    your own login on the Xero tab.
                  </p>
                </div>
              {/if}

              <SettingsAlerts />

              <slot />

              {#if resolvedShowSave && !$managedByOwner}
                <SettingsSaveBar
                  disabled={!$formValid}
                  loading={$loading}
                  onSave={saveSettings}
                />
              {/if}
            </div>
          </div>
        </div>
      </div>
    {/if}
  </div>
</Layout>

<style>
  .container {
    max-width: 1400px;
  }
</style>
